import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { StatusTone } from '@/components/ui/statusPill/statusTone.util.ts';
import { themeColors } from '@/config/theme/themeColors.ts';

type Props = {
  label: string;
  tone: StatusTone;
};

const tonePalette: Record<StatusTone, { background: string; color: string; dot: string }> = {
  accent: {
    background: themeColors.accentColor10,
    color: themeColors.accentColorDark,
    dot: themeColors.accentColor,
  },
  danger: { background: 'rgba(189,40,50,0.1)', color: themeColors.redDark, dot: themeColors.red },
  neutral: { background: themeColors.stroke, color: themeColors.greyDark, dot: themeColors.grey },
  new: { background: themeColors.blueLight, color: themeColors.blueDark, dot: themeColors.blue },
  progress: {
    background: 'rgba(216,109,80,0.12)',
    color: themeColors.orangeDark,
    dot: themeColors.orange,
  },
  success: {
    background: themeColors.greenLight,
    color: themeColors.greenDark,
    dot: themeColors.green,
  },
  warning: { background: 'rgba(247,181,0,0.16)', color: '#7A5A00', dot: themeColors.yellow },
};

export const StatusPill: React.FC<Props> = ({ label, tone }) => {
  const palette = tonePalette[tone];

  return (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        backgroundColor: palette.background,
        borderRadius: '999px',
        display: 'inline-flex',
        gap: 0.75,
        maxWidth: '100%',
        px: 1,
        py: 0.25,
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        component="span"
        sx={{
          backgroundColor: palette.dot,
          borderRadius: '50%',
          flexShrink: 0,
          height: 6,
          width: 6,
        }}
      />
      <Typography component="span" noWrap sx={{ color: palette.color }} variant="f12Medium">
        {label}
      </Typography>
    </Box>
  );
};

export const StatusDot: React.FC<Props> = ({ label, tone }) => (
  <Tooltip title={label}>
    <Box
      aria-label={label}
      component="span"
      sx={{
        backgroundColor: tonePalette[tone].dot,
        borderRadius: '50%',
        display: 'inline-block',
        flexShrink: 0,
        height: 8,
        width: 8,
      }}
    />
  </Tooltip>
);
